/**
 * The context extractors: the period vocabulary, the stage classifier, and the
 * builders that turn a page's data into the JSON the prompt is grounded on.
 *
 * This is the half of mediaChatService that decides WHAT the model is told;
 * the residual decides how it is asked.
 */

import { getIndustryConfig } from '../config/industryConfig';
import type {
  CappedAdSets, CappedCampaigns, ChannelData, ChannelSummary, ChatMediaData,
  DashboardContext, FunnelStage, IndustryContextJson, MMMBudgetEntry,
  MMMChatContextData, MMMContext, MMMSummaryScenario, PeriodInfo, PeriodKey,
  StageBreakdown, StageMixEntry,
} from './mediaChatTypes';

/**
 * Maps the app-level PeriodSelector values ("week" | "month" | "quarter")
 * to the canonical PeriodKey used in chat context.
 */
const APP_PERIOD_TO_KEY: Record<string, PeriodKey> = {
  week: '7d',
  month: '30d',
  quarter: '90d',
  // 'all' was missing, so the `?? '30d'` below silently relabelled an all-time
  // report "30 วันที่ผ่านมา" — ฿1.78M of spend tagged as a ฿717k window, on the
  // header, the saved /reports card title and the AI prompt at once.
  all: 'all',
};

const PERIOD_LABELS: Record<PeriodKey, string> = {
  '7d': '7 วันที่ผ่านมา',
  '30d': '30 วันที่ผ่านมา',
  '90d': '90 วันที่ผ่านมา',
  '12m': '12 เดือนที่ผ่านมา',
  all: 'ทั้งหมด',
};

/** Accepts any app-level period string and returns canonical PeriodInfo. */
export const toPeriodInfo = (appPeriod: string): PeriodInfo => {
  const key = APP_PERIOD_TO_KEY[appPeriod] ?? '30d';
  return { key, label: PERIOD_LABELS[key] };
};
/**
 * classifiedStage into this module's funnel vocabulary.
 *
 * The service classifies into 'aware' | 'consider' | 'convert' | 'unspecified'
 * (OBJECTIVE_STAGE, mediaIntelligenceService.ts:2026); this module and the
 * prompt speak 'awareness' | 'consideration' | 'conversion' | 'retention'.
 * Two vocabularies for one idea, so the mapping is explicit — and both spellings
 * are accepted, because a silent miss here reads as "unclassified", which is
 * how ฿944,358 of classified spend landed in one anonymous row.
 */
const STAGE_ALIASES: Record<string, FunnelStage> = {
  aware: 'awareness', awareness: 'awareness',
  consider: 'consideration', consideration: 'consideration',
  convert: 'conversion', conversion: 'conversion',
  retention: 'retention', retain: 'retention',
};

const toFunnelStage = (raw: string | undefined): FunnelStage | 'unclassified' =>
  STAGE_ALIASES[(raw ?? '').trim().toLowerCase()] ?? 'unclassified';
// ============================================================
// INDUSTRY CONTEXT (JSON)
// ============================================================

/**
 * Builds structured industry context from IndustryConfig.
 * Returns null when no industry key or industry is 'general'.
 */
const buildIndustryContextJson = (industry?: string): IndustryContextJson | null => {
  if (!industry) return null;
  const config = getIndustryConfig(industry);
  if (config.industry === 'general') return null;
  return {
    key: config.industry,
    labelEn: config.labelEn,
    labelTh: config.labelTh,
    kpiType: config.kpiType,
    cacRange: config.cacRange,
    avgConversionValue: config.avgConversionValue,
    hasRetentionStage: config.hasRetentionStage,
    kpis: config.kpis.map((k) => ({
      labelEn: k.labelEn,
      labelTh: k.label,
      higherIsBetter: k.higherIsBetter,
      unit: k.unit || 'count',
    })),
    optimizationWeights: config.optimizationWeights,
    confidence: config.confidence,
    proofPoints: config.proofPoints.map((p) => ({
      claimTh: p.claimTh,
      metric: p.metric,
    })),
  };
};

// ============================================================
// PAGE CONTEXT EXTRACTOR (structured JSON — YDMA-174)
// ============================================================

const safeNum = (n: number | undefined | null, fallback = 0): number =>
  typeof n === 'number' && !isNaN(n) ? n : fallback;

// 2 dp, because every surface prints rates at 2: rounding to 1 here printed an
// Awareness CTR of 0.14% as "0.10%" in the report and Google's 1.99% as "2.00%".
const pct = (num: number, denom: number): number =>
  denom > 0 ? Math.round((num / denom) * 10000) / 100 : 0;

/**
 * Falls back to deriving funnel stage from well-known channel IDs/names when
 * the caller doesn't explicitly tag a channel. Keeps the chat agent stage-aware
 * even when upstream demo data omits the stage field.
 */
const deriveStageFromChannel = (ch: ChannelData): FunnelStage | null => {
  if (ch.stage) return ch.stage;
  const key = (ch.id || ch.name || '').toLowerCase();
  if (/performance[ _-]?max|pmax/.test(key)) return 'conversion';
  if (/google[ _-]?search|search$/.test(key)) return 'consideration';
  if (/line|messenger|chat/.test(key)) return 'consideration';
  if (/google[ _-]?display|display|youtube|tiktok|facebook|instagram|reels/.test(key)) return 'awareness';
  if (/retention|crm|email|loyal/.test(key)) return 'retention';
  return null;
};

/**
 * How this channel's spend divides across funnel stages, biggest first.
 *
 * Spend-weighted rather than counted, because the two disagree and only one of
 * them is the question: this account has 21 conversion campaigns against 161
 * awareness ones, and the 21 carry 61% of the money.
 */
const buildStageMix = (ch: ChannelData): StageMixEntry[] | null => {
  const campaigns = ch.campaigns ?? [];
  if (campaigns.length === 0) return null;

  const byStage = new Map<FunnelStage | 'unclassified', number>();
  for (const c of campaigns) {
    const stage = toFunnelStage(c.classifiedStage);
    byStage.set(stage, (byStage.get(stage) ?? 0) + safeNum(c.cost));
  }
  // A channel that spent nothing this period still HAS campaigns, and they
  // still say what it is — TikTok spent ฿0 in the live window while running 18
  // conversion campaigns and no awareness one. Shares are zero rather than
  // absent, so the caller can still read the stage off them instead of falling
  // back to a guess from the channel's name.
  const total = [...byStage.values()].reduce((a, b) => a + b, 0);

  return [...byStage.entries()]
    .map(([stage, spend]) => ({
      stage,
      spend,
      spendShare: total > 0 ? Math.round((spend / total) * 1000) / 10 : 0,
    }))
    .sort((a, b) => b.spend - a.spend);
};

/**
 * The channel's stage, or null when its campaigns disagree.
 *
 * A platform that spans stages HAS no single stage, and naming one would be a
 * guess whichever way it fell — while the prompt uses the stage to decide which
 * KPI the model may judge on. Null plus stageMix says what is true.
 */
const resolveChannelStage = (ch: ChannelData, mix: StageMixEntry[] | null): FunnelStage | null => {
  if (ch.stage) return ch.stage;
  if (mix) {
    const named = mix.filter((m) => m.stage !== 'unclassified');
    if (named.length === 1) return named[0].stage as FunnelStage;
    return null;
  }
  // No campaigns at all — demo fixtures. The name-based guess is the documented
  // fallback for exactly that case, and only that case.
  return deriveStageFromChannel(ch);
};

const buildChannelSummary = (ch: ChannelData): ChannelSummary => {
  const stageMix = buildStageMix(ch);
  return ({
  id: ch.id,
  name: ch.name,
  icon: ch.icon ?? null,
  stage: resolveChannelStage(ch, stageMix),
  stageMix,
  impressions: ch.impressions,
  clicks: ch.clicks,
  ctr: pct(ch.clicks, ch.impressions),
  conversions: ch.conversions,
  cost: ch.cost,
  cpc: ch.cpc ?? 0,
  roas: ch.roas ?? null,
  // Denominator was ch.leads, which is ch.clicks — same number, but the name
  // told the assistant it was dividing by a lead count it could reason about.
  // NOT `ch.clicks || 1`: that turned a view-through channel (0 clicks, real
  // conversions — normal on Facebook and Google) into conversions x 100 percent,
  // e.g. 500%, while buildStageBreakdown reported 0% for the same channel in the
  // same payload. pct() already returns 0 when the denominator is <= 0.
  conversionRate: pct(ch.conversions, ch.clicks),
  // undefined -> null, but a real 0 survives as 0: a platform that reports the
  // metric and recorded none of it is a measurement, and this codebase keeps
  // that distinct from a platform that does not report it at all.
  engagements: ch.engagements ?? null,
  videoViews: ch.videoViews ?? null,
  // Same `?? null` as above and for a stronger reason: safeNum would turn a
  // null into 0 and tell the model this channel produced no leads, when what
  // actually happened is that nobody measured it for leads.
  leadConversions: ch.leadConversions ?? null,
  campaigns: buildCampaigns(ch),
  adSets: buildAdSets(ch),
  });
};

/**
 * The ad sets worth handing to the model, biggest spender first.
 *
 * Every ad set used to travel: 843 on the live workspace, 86,700 of the
 * context's 88,433 characters — 98% of every message, charged every turn. Only
 * 145 of them spent anything in the window and 97 cleared the ฿1,000 floor the
 * prompt makes mandatory before any recommendation, so roughly 700 rows were
 * noise the model was required to ignore and could not rank.
 *
 * Zero-spend rows are dropped rather than capped away: they cannot clear that
 * floor, so no recommendation could ever rest on one.
 *
 * Ranked by SPEND, not by performance. The insights block that ranks creatives
 * best-first can only ever show the good ones, which is how its "which should I
 * replace" button came to be unanswerable. Money at stake is the axis the
 * prompt actually asks for and it cuts both ways — the worst big spender is as
 * visible as the best.
 */
const AD_SET_CAP = 20;

/**
 * Campaigns are capped the same way and for the same reason as ad sets: by
 * spend, biggest first, zero-spend rows dropped. 451 campaigns are not archived
 * on this account and 126 of them spent anything in the last 30 days, so an
 * uncapped list would be three quarters noise the prompt forbids acting on.
 *
 * Lower than AD_SET_CAP because a campaign row is wider — it carries status,
 * two budgets and two dates — and because a channel's spend concentrates
 * higher up: the top 15 campaigns cover more of the money than the top 15 ad
 * sets do.
 */
const CAMPAIGN_CAP = 15;

/** Anything the platform does not call 'active' is money a change cannot move. */
const isRunning = (status: string | undefined): boolean =>
  (status ?? '').trim().toLowerCase() === 'active';

const buildCampaigns = (ch: ChannelData): CappedCampaigns | null => {
  const all = ch.campaigns ?? [];
  if (all.length === 0) return null;

  const spending = all
    .filter((c) => safeNum(c.cost) > 0)
    .sort((a, b) => safeNum(b.cost) - safeNum(a.cost));

  const shown = spending.slice(0, CAMPAIGN_CAP);
  const omittedRows = [
    ...spending.slice(CAMPAIGN_CAP),
    ...all.filter((c) => safeNum(c.cost) <= 0),
  ];

  return {
    shown: shown.map((c) => ({
      name: c.name ?? '(unnamed)',
      stage: toFunnelStage(c.classifiedStage),
      // null, not 'unknown' and not 'active': a status the sync never wrote is
      // not a status, and defaulting it either way is a claim about the
      // account. The prompt is told to treat null as "cannot tell".
      status: c.status ?? null,
      dailyBudget: c.dailyBudget ?? null,
      lifetimeBudget: c.lifetimeBudget ?? null,
      startDate: c.startDate ?? null,
      endDate: c.endDate ?? null,
      impressions: safeNum(c.impressions),
      clicks: safeNum(c.clicks),
      conversions: safeNum(c.conversions),
      cost: safeNum(c.cost),
    })),
    omitted: omittedRows.length,
    omittedSpend: Math.round(omittedRows.reduce((t, c) => t + safeNum(c.cost), 0)),
    total: all.length,
    // Counted over ALL campaigns, not the shown ones. A campaign with an
    // unknown status is not counted as stopped — null means "cannot tell",
    // and this figure is quoted as a fact about money.
    stoppedSpend: Math.round(
      all
        .filter((c) => c.status !== undefined && c.status !== null && !isRunning(c.status))
        .reduce((t, c) => t + safeNum(c.cost), 0),
    ),
  };
};

const buildAdSets = (ch: ChannelData): CappedAdSets | null => {
  const all = ch.adSets ?? [];
  if (all.length === 0) return null;

  const spending = all
    .filter((a) => safeNum(a.cost) > 0)
    .sort((a, b) => safeNum(b.cost) - safeNum(a.cost));

  const shown = spending.slice(0, AD_SET_CAP);
  const omittedRows = [
    ...spending.slice(AD_SET_CAP),
    ...all.filter((a) => safeNum(a.cost) <= 0),
  ];

  return {
    shown: shown.map((a) => ({
      name: a.name,
      stage: a.stage ?? null,
      impressions: safeNum(a.impressions),
      clicks: safeNum(a.clicks),
      conversions: safeNum(a.conversions),
      cost: safeNum(a.cost),
      leadConversions: a.leadConversions ?? null,
    })),
    omitted: omittedRows.length,
    omittedSpend: Math.round(omittedRows.reduce((t, a) => t + safeNum(a.cost), 0)),
    total: all.length,
  };
};

/**
 * Extracts visible dashboard data as a structured JSON object.
 * The LLM receives this directly — no ASCII-art formatting.
 *
 * Issue: YDMA-174
 */
export const extractPageContext = (
  data: ChatMediaData,
  selectedChannelId: string | null,
  appPeriod: string = 'month'
): DashboardContext => {
  const { metrics, channels, trends, brokers, mmmRecommendations } = data;
  const period = toPeriodInfo(appPeriod);

  const channelSummaries = channels.map(buildChannelSummary);

  const selectedChannel = selectedChannelId
    ? channelSummaries.find((c) => c.id === selectedChannelId) ?? null
    : null;

  // Ranking on ROAS only makes sense among channels that reported revenue.
  // Including the rest put an unmeasured channel at the bottom of the list and
  // the report named it the worst performer.
  const measuredByROAS = channelSummaries
    .filter((c): c is typeof c & { roas: number } => typeof c.roas === 'number' && c.roas > 0)
    .sort((a, b) => b.roas - a.roas);
  const bestPerformer = measuredByROAS[0]
    ? { name: measuredByROAS[0].name, roas: measuredByROAS[0].roas }
    : null;
  const worstPerformer = measuredByROAS.length > 1
    ? { name: measuredByROAS[measuredByROAS.length - 1].name, roas: measuredByROAS[measuredByROAS.length - 1].roas }
    : null;

  const mmmStatus = mmmRecommendations
    ? {
        confidence: mmmRecommendations.confidence ?? 0,
        dataDays: mmmRecommendations.status?.dataDays ?? 0,
        recommendedAction: mmmRecommendations.option1?.action ?? '',
        expectedImpact: mmmRecommendations.option1?.impact ?? '',
        keyInsight: mmmRecommendations.keyInsight ?? '',
      }
    : null;

  // Summarised from stored columns only. The previous version reported a lead
  // count and an average conversion rate, both of which the service had
  // manufactured — the assistant was being handed invented figures as fact.
  const brokerConversions = brokers?.reduce((s, b) => s + (b.conversions ?? 0), 0) ?? 0;
  const brokerSpend = brokers?.reduce((s, b) => s + (b.spend ?? 0), 0) ?? 0;
  const brokerSummary = brokers && brokers.length > 0
    ? {
        total: brokers.length,
        totalConversions: brokerConversions,
        totalSpend: brokerSpend,
        costPerConversion: brokerConversions > 0 ? Math.round(brokerSpend / brokerConversions) : 0,
        // Sorted before slicing: the producer's query has no ORDER BY, so an
        // unsorted slice labelled "top" handed the model an arbitrary ten — with
        // the worst-performing broker able to lead the list.
        topBrokers: [...brokers]
          .sort((a, b) => (b.conversions ?? 0) - (a.conversions ?? 0))
          .slice(0, 10)
          .map((b) => ({
            name: b.name,
            type: b.type,
            conversions: b.conversions,
            spend: b.spend,
            roi: b.roi,
          })),
      }
    : null;

  const trendSummary = trends
    ? {
        clicks: trends.clicks ?? [],
        conversions: trends.conversions ?? [],
        costPerConversion: trends.costPerConversion ?? [],
        spend: trends.spend ?? [],
        buckets: trends.buckets ?? [],
      }
    : null;

  const stageBreakdown = buildStageBreakdown(channelSummaries, channels);

  return {
    kind: 'dashboard',
    period,
    metrics: metrics
      ? {
          conversions: safeNum(metrics.conversions),
          conversionsGrowth: metrics.conversionsGrowth ?? null,
          conversionRate: safeNum(metrics.conversionRate),
          // metrics.costPerConversion is cost / conversions — the same figure the page labels
          // "Cost per Conversion". It used to be overwritten with
          // sum(cost) / sum(channel.leads), i.e. cost per CLICK, on the theory
          // that it matched the page. It did not: the page divides by
          // conversions, so the assistant was quoting a different number under
          // a name that implied a third metric again.
          costPerConversion: safeNum(metrics.cpl),
          costPerConversionChange: metrics.cplChange ?? null,
          impressions: safeNum(metrics.impressions),
          ctr: safeNum(metrics.ctr),
        }
      : null,
    channels: channelSummaries,
    selectedChannel,
    bestPerformer,
    worstPerformer,
    mmmStatus,
    brokers: brokerSummary,
    trends: trendSummary,
    stageBreakdown,
    // `?? null` and never `?? []`: an empty array tells the model the pipeline
    // was checked and is fine, which is a stronger claim than "the page did not
    // pass this" and the opposite of what an undefined means here.
    systemHealth: data.systemHealth ?? null,
    industry: buildIndustryContextJson(data.industry),
  };
};

// ============================================================
// STAGE BREAKDOWN AGGREGATOR
// ============================================================

const STAGE_LABEL_TH: Record<FunnelStage | 'unclassified', string> = {
  awareness: '📢 Awareness',
  consideration: '🔍 Consideration',
  conversion: '🎯 Conversion',
  retention: '🔁 Retention',
  unclassified: '❓ Unclassified',
};

/**
 * Named metrics only where this product actually carries them.
 *
 * These strings are data: they travel in stageBreakdown[].primaryKpi and are also
 * printed verbatim into the report's stage table. Naming Frequency, Reach, LPV,
 * Repeat Rate and CLV Uplift here put them in front of the model as fact, which
 * is why they kept surfacing after the prompt prose was corrected. Frequency and
 * LPV are not stored at all; the field that used to be called reach holds
 * impressions; retention measures do not exist anywhere in this schema.
 */
const STAGE_PRIMARY_KPI: Record<FunnelStage | 'unclassified', string> = {
  awareness: 'CPM + CTR (not cost per conversion)',
  consideration: 'CTR + CPC',
  conversion: 'Cost per Conversion + CVR + ROAS',
  retention: 'not measured here — report spend and conversions only',
  unclassified: 'evaluate per channel — stage not tagged',
};

/**
 * Aggregates channels by funnel stage so the AI can evaluate metrics in
 * stage-appropriate context. Awareness costing more per conversion is normal — judging it
 * with Conversion-stage benchmarks would mis-flag healthy spend.
 */
/** One row's worth of totals, whatever grain produced it. */
interface StageBucket {
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  channelNames: Set<string>;
}

const emptyBucket = (): StageBucket => ({
  spend: 0, impressions: 0, clicks: 0, conversions: 0, channelNames: new Set<string>(),
});

/**
 * Stage totals, built from CAMPAIGNS where they are available.
 *
 * Grouping by channel put this account's ฿395,214 of Facebook conversion spend
 * under Awareness and left the Conversion row empty, because a channel got one
 * stage from its own name. Campaigns carry the stage the service classified
 * from the stored objective, so the money lands where it belongs.
 *
 * Falls back to grouping channels when no campaign reached the context — demo
 * fixtures — which is the only case that ever had a single stage per channel.
 */
const buildStageBreakdown = (
  channels: ChannelSummary[],
  raw: ChannelData[] = [],
): StageBreakdown[] | null => {
  if (!channels.length) return null;

  const grouped = new Map<FunnelStage | 'unclassified', StageBucket>();
  const add = (key: FunnelStage | 'unclassified', channelName: string, row: {
    cost?: number; impressions?: number; clicks?: number; conversions?: number;
  }) => {
    const b = grouped.get(key) ?? emptyBucket();
    b.spend += safeNum(row.cost);
    b.impressions += safeNum(row.impressions);
    b.clicks += safeNum(row.clicks);
    b.conversions += safeNum(row.conversions);
    b.channelNames.add(channelName);
    grouped.set(key, b);
  };

  const hasCampaigns = raw.some((c) => (c.campaigns?.length ?? 0) > 0);
  if (hasCampaigns) {
    for (const ch of raw) {
      for (const c of ch.campaigns ?? []) add(toFunnelStage(c.classifiedStage), ch.name, c);
    }
  } else {
    for (const ch of channels) add(ch.stage ?? 'unclassified', ch.name, ch);
  }

  const totalSpend = [...grouped.values()].reduce((s, b) => s + b.spend, 0);
  const stageOrder: Array<FunnelStage | 'unclassified'> = [
    'awareness', 'consideration', 'conversion', 'retention', 'unclassified',
  ];
  const out: StageBreakdown[] = [];
  for (const stage of stageOrder) {
    const b = grouped.get(stage);
    if (!b) continue;
    const { spend, impressions, clicks, conversions } = b;
    out.push({
      stage,
      stageLabelTh: STAGE_LABEL_TH[stage],
      channelCount: b.channelNames.size,
      spend,
      impressions,
      clicks,
      conversions,
      ctr: impressions > 0 ? pct(clicks, impressions) : null,
      cpc: clicks > 0 ? Math.round((spend / clicks) * 100) / 100 : null,
      costPerConversion: conversions > 0 ? Math.round((spend / conversions) * 100) / 100 : null,
      cpm: impressions > 0 ? Math.round((spend / impressions) * 1000 * 100) / 100 : null,
      conversionRate: clicks > 0 ? pct(conversions, clicks) : null,
      spendShare: totalSpend > 0 ? Math.round((spend / totalSpend) * 1000) / 10 : 0,
      primaryKpi: STAGE_PRIMARY_KPI[stage],
      channelNames: [...b.channelNames],
    });
  }
  return out;
};

// ============================================================
// MMM CONTEXT EXTRACTOR (structured JSON — YDMA-174)
// ============================================================

const buildScenario = (
  key: 'option1' | 'option2',
  s: NonNullable<NonNullable<MMMChatContextData['scenarios']>[typeof key]>,
  selected: 'option1' | 'option2' | undefined,
  revenueAvailable: boolean,
): MMMSummaryScenario => ({
  key,
  label: s.label,
  action: s.action,
  impact: s.impact,
  explanation: s.explanation ?? '',
  projectedRevenue: revenueAvailable ? s.projectedRevenue : null,
  spend: s.spend,
  roi: revenueAvailable ? s.roi : null,
  change: s.change ?? '',
  isSelected: selected === key,
});

/**
 * Builds structured MMM context the AI reasons over (YDMA-174).
 */
export const extractMMMContext = (mmm: MMMChatContextData): MMMContext => {
  const { status, confidence, baseline, selectedScenario, scenarios, budgetBreakdown, synergies, keyInsight, liveBudgetOverrides } = mmm;

  // Absent, this defaults to FALSE — the safe direction. A missing flag means
  // the caller did not say, and reporting "no revenue figure here" is recoverable
  // in a way that reporting ฿0 of revenue is not.
  const revenueAvailable = mmm.revenueAvailable === true;

  const scenarioList: MMMSummaryScenario[] = [];
  if (scenarios?.option1) scenarioList.push(buildScenario('option1', scenarios.option1, selectedScenario, revenueAvailable));
  if (scenarios?.option2) scenarioList.push(buildScenario('option2', scenarios.option2, selectedScenario, revenueAvailable));

  const budgetEntries: MMMBudgetEntry[] = (budgetBreakdown ?? []).map((b) => {
    const override = liveBudgetOverrides?.[b.name];
    return {
      name: b.name,
      currentBudget: b.currentBudget,
      recommendedBudget: override ?? b.recommendedBudget,
      currentPercent: b.currentPercent,
      recommendedPercent: b.recommendedPercent,
      delta: (override ?? b.recommendedBudget) - b.currentBudget,
      hasOverride: override !== undefined,
      overrideValue: override ?? null,
    };
  });

  return {
    kind: 'mmm',
    mmmStatus: {
      confidence: confidence ?? null,
      dataDays: status?.dataDays ?? null,
      spanDays: status?.spanDays ?? null,
    },
    baseline: baseline
      ? {
          spend: baseline.spend,
          revenue: revenueAvailable ? baseline.revenue : null,
          roi: revenueAvailable ? baseline.roi : null,
          revenueSpend: revenueAvailable ? (mmm.revenueSpend ?? null) : null,
          revenueCoveragePct: revenueAvailable ? (mmm.revenueCoveragePct ?? null) : null,
        }
      : null,
    selectedScenario: selectedScenario ?? null,
    scenarios: scenarioList,
    budgetBreakdown: budgetEntries,
    synergies: synergies ?? [],
    keyInsight: keyInsight ?? null,
    systemHealth: mmm.systemHealth ?? null,
    industry: buildIndustryContextJson(mmm.industry),
  };
};
